import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Heart, Star, Eye, Award, Sparkles, TrendingUp, Zap } from 'lucide-react';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Tooltip } from './ui/Tooltip';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { Product } from '../utils/types';
interface ProductCardProps {
  product: Product;
  featured?: boolean;
  index?: number;
  onQuickView?: (product: Product) => void;
}
export function ProductCard({
  product,
  featured = false,
  index = 0,
  onQuickView
}: ProductCardProps) {
  const {
    addToCart
  } = useCart();
  const {
    toggleWishlist,
    isInWishlist
  } = useWishlist();
  const inWishlist = isInWishlist(product.id);
  const isBestSeller = (product.rating || 0) >= 4.8 && (product.reviewCount || 0) > 50;
  const isNew = product.isNew;
  const discountPercentage = product.originalPrice ? Math.round((product.originalPrice - product.price) / product.originalPrice * 100) : 0;
  // Mock nutritional data if not present
  const protein = product.nutritionalInfo?.crudeProtein?.replace('%', '') || '28';
  const fat = product.nutritionalInfo?.crudeFat?.replace('%', '') || '15';
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
  };
  const handleToggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product);
  };
  const handleQuickView = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onQuickView?.(product);
  };
  return <motion.article initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.05
  }} className="group relative h-full" data-id="element-3956">
      <Link to={`/productos/${product.id}`} className="block h-full focus:outline-none" data-id="element-3957">
        <div className={`relative h-full bg-white rounded-[2rem] border transition-all duration-300 flex flex-col overflow-hidden border-gray-100 shadow-soft-sm hover:shadow-soft-xl hover:border-primary-100`} data-id="element-3958">
          {/* Image Container */}
          <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100" data-id="element-3959">
            {/* Badges */}
            <div className="absolute top-4 left-4 z-20 flex flex-col gap-2 items-start" data-id="element-3960">
              {isNew && <Badge variant="primary" size="sm" className="shadow-lg backdrop-blur-md bg-primary-600/90 text-white border-none font-bold px-3" data-id="element-3961">
                  <Sparkles className="w-3 h-3 mr-1" data-id="element-3962" /> Nuevo
                </Badge>}
              {discountPercentage > 0 && <Badge variant="secondary" size="sm" className="shadow-lg backdrop-blur-md bg-rose-500/90 text-white border-none font-bold px-3" data-id="element-3963">
                  -{discountPercentage}%
                </Badge>}
              {isBestSeller && <Badge variant="warning" size="sm" className="shadow-lg backdrop-blur-md bg-amber-400/90 text-white border-none font-bold px-3" data-id="element-3964">
                  <Award className="w-3 h-3 mr-1" data-id="element-3965" /> Top Ventas
                </Badge>}
            </div>

            {/* Actions (Visible on Hover) */}
            <div className="absolute top-4 right-4 z-20 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-4 group-hover:translate-x-0" data-id="element-3966">
              <Tooltip content={inWishlist ? 'Quitar de favoritos' : 'Añadir a favoritos'} position="left" data-id="element-3967">
                <button onClick={handleToggleWishlist} className={`p-2.5 rounded-full backdrop-blur-md shadow-md border border-white/20 transition-all ${inWishlist ? 'bg-rose-50 text-rose-500 scale-110' : 'bg-white/90 text-gray-400 hover:text-rose-500 hover:scale-110'}`} data-id="element-3968">
                  <Heart className={`w-4 h-4 transition-all ${inWishlist ? 'fill-current' : ''}`} data-id="element-3969" />
                </button>
              </Tooltip>

              {onQuickView && <Tooltip content="Vista rápida" position="left" data-id="element-3970">
                  <button onClick={handleQuickView} className="p-2.5 rounded-full backdrop-blur-md shadow-md border border-white/20 bg-white/90 text-gray-400 hover:text-primary-600 transition-all hover:scale-110" data-id="element-3971">
                    <Eye className="w-4 h-4" data-id="element-3972" />
                  </button>
                </Tooltip>}
            </div>

            {/* Image */}
            <img src={product.image} alt={product.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 mix-blend-multiply" data-id="element-3973" />

            {/* Quick Add Overlay */}
            <div className="absolute inset-x-0 bottom-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300 z-10 ease-out" data-id="element-3974">
              <Button variant="primary" fullWidth onClick={handleAddToCart} disabled={!product.inStock} className="rounded-xl shadow-lg bg-white/95 backdrop-blur text-charcoal-900 hover:bg-white border-none font-bold" data-id="element-3975">
                {product.inStock ? <>
                    <ShoppingCart className="w-4 h-4 mr-2" data-id="element-3976" /> Añadir al Carrito
                  </> : 'Agotado'}
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 flex-1 flex flex-col" data-id="element-3977">
            {/* Category & Rating */}
            <div className="flex items-center justify-between mb-3 gap-2" data-id="element-3978">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50 px-2 py-1 rounded-md truncate" data-id="element-3979">
                {product.subcategory || product.category}
              </span>
              {product.rating && <div className="flex items-center gap-1.5 bg-amber-50 px-2 py-1 rounded-full border border-amber-100 flex-shrink-0" data-id="element-3980">
                  <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-3981" />
                  <span className="text-xs font-bold text-charcoal-700" data-id="element-3982">
                    {product.rating}
                  </span>
                  <span className="text-[10px] text-gray-400" data-id="element-3983">
                    ({product.reviewCount})
                  </span>
                </div>}
            </div>

            <h3 className="text-lg font-bold text-charcoal-900 font-serif leading-tight mb-3 group-hover:text-primary-700 transition-colors line-clamp-2 min-h-[3rem]" data-id="element-3984">
              {product.name}
            </h3>

            {/* Enhanced Nutritional Visualization (Only for food) */}
            {(product.category === 'dog-food' || product.category === 'cat-food') && <div className="mb-4 space-y-2" data-id="element-3985">
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl p-3 border border-emerald-200/50 relative overflow-hidden" data-id="element-3986">
                  <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-200/30 rounded-full blur-2xl" data-id="element-3987"></div>
                  <div className="relative z-10" data-id="element-3988">
                    <div className="flex justify-between items-center mb-2" data-id="element-3989">
                      <div className="flex items-center gap-1.5" data-id="element-3990">
                        <TrendingUp className="w-3.5 h-3.5 text-emerald-600" data-id="element-3991" />
                        <span className="text-[10px] text-emerald-700 font-bold uppercase tracking-wide" data-id="element-3992">
                          Proteína
                        </span>
                      </div>
                      <span className="text-sm font-bold text-emerald-700" data-id="element-3993">
                        {protein}%
                      </span>
                    </div>
                    <div className="h-2 bg-emerald-200/50 rounded-full overflow-hidden" data-id="element-3994">
                      <motion.div initial={{
                    width: 0
                  }} whileInView={{
                    width: `${protein}%`
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.8,
                    ease: 'easeOut',
                    delay: 0.2
                  }} className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full shadow-sm" data-id="element-3995" />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-xl p-3 border border-amber-200/50 relative overflow-hidden" data-id="element-3996">
                  <div className="absolute top-0 right-0 w-16 h-16 bg-amber-200/30 rounded-full blur-2xl" data-id="element-3997"></div>
                  <div className="relative z-10" data-id="element-3998">
                    <div className="flex justify-between items-center mb-2" data-id="element-3999">
                      <div className="flex items-center gap-1.5" data-id="element-4000">
                        <Zap className="w-3.5 h-3.5 text-amber-600" data-id="element-4001" />
                        <span className="text-[10px] text-amber-700 font-bold uppercase tracking-wide" data-id="element-4002">
                          Grasa
                        </span>
                      </div>
                      <span className="text-sm font-bold text-amber-700" data-id="element-4003">
                        {fat}%
                      </span>
                    </div>
                    <div className="h-2 bg-amber-200/50 rounded-full overflow-hidden" data-id="element-4004">
                      <motion.div initial={{
                    width: 0
                  }} whileInView={{
                    width: `${fat}%`
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.8,
                    ease: 'easeOut',
                    delay: 0.3
                  }} className="h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-full shadow-sm" data-id="element-4005" />
                    </div>
                  </div>
                </div>
              </div>}

            {/* Footer: Price & Stock */}
            <div className="mt-auto pt-4 border-t border-gray-50" data-id="element-4006">
              <div className="flex items-end justify-between mb-2" data-id="element-4007">
                <div data-id="element-4008">
                  {product.originalPrice && product.originalPrice > product.price && <span className="text-xs text-gray-400 line-through block mb-0.5" data-id="element-4009">
                        {product.originalPrice.toFixed(2)}€
                      </span>}
                  <span className="text-xl font-bold text-charcoal-900 font-serif" data-id="element-4010">
                    {product.price.toFixed(2)}€
                  </span>
                </div>

                {/* Visual Stock Indicator */}
                <div className="text-right" data-id="element-4011">
                  <div className="flex items-center justify-end gap-1.5 mb-1" data-id="element-4012">
                    <div className={`w-2 h-2 rounded-full ${product.inStock ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} data-id="element-4013" />
                    <span className="text-[10px] font-medium text-gray-500" data-id="element-4014">
                      {product.inStock ? 'Disponible' : 'Agotado'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </motion.article>;
}